<template>
  <div
    :tabindex="disabled ? '-1' : '0'"
    :class="[
      'zm-multi-select',
      $slots.button ? '' : 'zm-multi-select-button',
      disabled ? 'is-disabled' : ''
    ]"
    :role="disabled ? '' : 'button'"
    :aria-expanded="visible.toString()"
    :aria-label="placeholder"
    @click="openDropdown"
    @keydown.enter.self="openDropdown"
    @keydown.space.self.prevent="openDropdown"
  >
    <slot
      v-if="$slots.button"
      name="button"
    />
    <div
      v-else-if="selectedIdListLocal.length === 0"
      class="zm-multi-select-button__placeholder"
    >
      {{ placeholder }}
    </div>
    <div
      v-else-if="
        allSelectedLabel &&
          selectedIdListLocal.length === options.length &&
          !hasMore
      "
      class="zm-multi-select-button__placeholder"
    >
      {{ allSelectedLabel }}
    </div>

    <selected-list
      v-else-if="selectedIdListLocal.length"
      :options="options"
      :selected-id-list="selectedIdListLocal"
      :selected-data-list="value"
      :value-key="valueKey"
      :name-key="nameKey"
      :max-line="maxLine"
      :disabled="disabled"
      :collapse-tags="collapseTags"
      @delete="handleDeleteSelected"
    />
    <transition
      name="zm-zoom-in-top"
      @after-enter="afterEnter"
      @after-leave="afterLeave"
    >
      <multi-select-dropdown
        v-if="visible"
        ref="dropdown"
        v-clickoutside="closeDropdown"
        :options="options"
        :value-key="valueKey"
        :name-key="nameKey"
        :disabled-key="disabledKey"
        :clear-visible="clearVisible"
        :search-visible="searchVisible"
        :button-visible="buttonVisible"
        :filter-all-visible="filterAllVisible"
        :search-placeholder="searchPlaceholder"
        :filter-all-label="filterAllLabel"
        :confirm-label="confirmLabel"
        :clear-label="clearLabel"
        :has-more="hasMore"
        :loading="loading"
        :init-select="selectedIdListLocal"
        :remote-search="!!remoteMethod"
        :style="{
          width: (poperWidth || buttonWidth) + 'px'
        }"
        @load-data="handleLoadData"
        @data-change="handleDataChange"
        @apply-close="visible = false"
      >
        <slot
          slot="header"
          name="header"
        />
        <slot
          slot="footer"
          name="footer"
        />
        <slot
          slot="applyButton"
          name="apply-button"
        />
        <slot
          slot="clearButton"
          name="clear-button"
        />
        <template
          slot="option"
          slot-scope="props"
        >
          <slot
            name="option"
            :item="props.item"
          />
        </template>
      </multi-select-dropdown>
    </transition>
  </div>
</template>

<script>
  import Clickoutside from '../../utils/clickoutside';
  import MultiSelectDropdown from './multi-select-plain-dropdown.vue';
  import SelectedList from './multi-select-plain-list.vue';
  export default {
    name: 'ZmMultiSelect',
    components: { MultiSelectDropdown, SelectedList },
    directives: {
      Clickoutside
    },
    props: {
      value: {
        type: Array,
        default() {
          return [];
        }
      },
      options: {
        type: Array,
        default() {
          return [];
        }
      },
      disabled: Boolean,
      valueKey: {
        type: String,
        default: 'id'
      },
      nameKey: {
        type: String,
        default: 'label'
      },
      disabledKey: {
        type: String,
        default: 'disabled'
      },
      convertAllToEmpty: Boolean,
      hasMore: Boolean,
      remoteMethod: {
        type: Function
      },
      clearVisible: {
        type: Boolean,
        default: true
      },
      searchVisible: {
        type: Boolean,
        default: true
      },
      buttonVisible: {
        type: Boolean,
        default: true
      },
      filterAllVisible: {
        type: Boolean,
        default: true
      },
      placeholder: {
        type: String,
        default: 'Please select'
      },
      allSelectedLabel: {
        type: String,
        default: ''
      },
      searchPlaceholder: {
        type: String,
        default: 'Search filters'
      },
      filterAllLabel: {
        type: String,
        default: 'Filter (All)'
      },
      confirmLabel: {
        type: String,
        default: 'Apply'
      },
      clearLabel: {
        type: String,
        default: 'Clear'
      },
      collapseTags: Boolean,
      maxLine: {
        type: Number,
        default: 1
      },
      poperWidth: {
        type: Number,
        default: 0
      },
      beforeChange: {
        type: Function,
        default: undefined
      }
    },
    data() {
      return {
        visible: false,
        loading: false,
        hasConvertAllToEmpty: false,
        buttonWidth: 100
      };
    },
    computed: {
      selectedIdListLocal() {
        if (this.hasConvertAllToEmpty) {
          return this.options.map(item => item[this.valueKey]);
        }
        return this.value.map(item => item[this.valueKey]);
      }
    },
    watch: {
      visible(visible) {
        this.$emit('visible-change', visible);
        if (!this.$el) {
          return;
        }
        if (!visible) {
          this.$el.focus();
        } else {
          this.buttonWidth = Math.max(this.$el.getBoundingClientRect().width, 140);
        }
      },
      value() {
        if (this.value.length !== 0) {
          this.hasConvertAllToEmpty = false;
        }
      }
    },
    methods: {
      afterEnter() {
        this.$refs.dropdown?.initAriaDialog();
      },
      afterLeave() {
        this.$refs.dropdown?.closeAriaDialog();
      },
      openDropdown() {
        if (this.disabled) {
          return;
        }
        clearTimeout(this._timer);
        if (this.visible) {
          this.visible = false;
          return;
        }
        this._timer = setTimeout(() => {
          this.visible = true;
        }, 30);
      },
      closeDropdown() {
        clearTimeout(this._timer);
        this._timer = setTimeout(() => {
          this.visible = false;
        }, 30);
      },
      handleLoadData(filterText) {
        // stop when remoteMethod not been function
        if (typeof this.remoteMethod !== 'function') {
          return;
        }
        // stop when props set hasMore = false, and filter string did not changed
        if (!this.hasMore && this._filterText === filterText) {
          return;
        }

        this.loading = true;

        this._currentPage = this._currentPage ? this._currentPage + 1 : 1;
        // restart from first page when filter string has been changed
        if (this._filterText !== filterText) {
          this._currentPage = 1;
        }
        this._filterText = filterText;

        const remoteMethodReturns = this.remoteMethod(this._currentPage, filterText);
        if (!remoteMethodReturns || !remoteMethodReturns.finally) {
          console.warn('remote-method need return Promise object.');
          return;
        }

        remoteMethodReturns.finally(() => {
          this.loading = false;
        });
      },
      handleDataChange(selectedIDList) {
        // fix select all to empty
        if (this.convertAllToEmpty && selectedIDList.length === this.options.length && !this.hasMore) {
          selectedIDList = [];
          this.hasConvertAllToEmpty = true;
        } else {
          this.hasConvertAllToEmpty = false;
        }
        // convert key to number if need
        const needConvertKeyToNumber = this.options[0] && typeof this.options[0][this.valueKey] === 'number';
        selectedIDList = selectedIDList.map(key => {
          return needConvertKeyToNumber ? parseInt(key, 10) : key;
        });
        // trigger data change
        this.triggerDataChange(selectedIDList);
      },
      handleDeleteSelected(deleteId) {
        const newSelectedIdList = this.selectedIdListLocal.filter(id => id !== deleteId);
        this.triggerDataChange(newSelectedIdList);
        this.$el.focus();
      },
      triggerDataChange(idList) {
        const newValue = this.options.filter((item) => {
          return idList.indexOf(item[this.valueKey]) > -1;
        });
        if (typeof this.beforeChange === 'function') {
          const returns = this.beforeChange(newValue);
          if (typeof returns === 'boolean' && returns === false) {
            return;
          }
        }
        this.$emit('input', newValue);
        this.$emit('data-change', newValue);
      }
    }
  };
</script>

<style lang='scss'>
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";
  @import "../../scss/common/transition";
  @import "../../scss/mixins/utils";
  @import "../../components/input/var";

  @include b(multi-select) {
    display: inline-block;
  }
  @include b(multi-select-button) {
    position: relative;
    min-width: 240px;
    max-width: 100%;
    border: $--input-border;
    border-radius: $--input-border-radius;
    transition: $--border-transition-base;
    font-size: $--font-size-base;
    outline: none;

    @include e(placeholder) {
      min-height: $--input-height;
      padding: 0 $--input-default-padding-horizontal;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: left;
      line-height: $--input-height;
      color: $--input-placeholder-color;
    }
    .tag-list {
      display: flex;
      gap: 4px;
      flex-wrap: wrap;
      padding: 4px;
    }
    &:focus {
      box-shadow: $--base-focus-shadow;
    }
    @include when(disabled) {
      border-color: $--input-border-color-disabled;
      cursor: not-allowed;
      &:focus {
        box-shadow: none;
      }
      @include e(placeholder) {
        color: $--input-disabled-placeholder-color;
      }
    }
  }
  .zm-theme--dark {
    @include b(multi-select-button) {
      border-color: $--input-dark-border-color-filled;
      &:focus {
        box-shadow: $--base-focus-shadow-dark;
      }
      @include e(placeholder) {
        color: $--input-dark-placeholder-color;
      }
      @include when(disabled) {
        &:focus {
          box-shadow: none;
        }
        border-color: $--input-dark-border-color-disabled;
        color: $--input-dark-color-disabled;
        @include e(placeholder) {
          color: $--input-dark-disabled-placeholder-color;
        }
      }
    }
  }
</style>
